// ─────────────────────────────────────────────────────────────────────────────
// numbers — display formatting for numeric values (#2068).
//
// Before this module there were 26 raw `.toFixed()` calls and two competing
// one-decimal helpers that disagreed about what to do with the awkward inputs:
//
//   RoundSummaryCard.oneDecimal  →  "—" for anything non-finite
//   ShotCalendar.fmtAvg          →  drops the ".0" on whole numbers
//
// Neither is wrong; they answer different questions, so both survive here
// under names that say which is which. The bare `.toFixed()` calls do a third
// thing again — they render "NaN" — which is why migrating a call site is a
// behaviour decision, not a mechanical substitution.
// ─────────────────────────────────────────────────────────────────────────────

/** Em-dash placeholder rendered wherever a statistic has no value. */
export const NO_VALUE = "—";

/**
 * One decimal place, e.g. 9.47 → "9.5". Non-finite input (null, undefined,
 * NaN, ±Infinity) renders `fallback` rather than the string "NaN".
 */
export function oneDecimal(n, fallback = NO_VALUE) {
  return Number.isFinite(n) ? n.toFixed(1) : fallback;
}

/**
 * Two decimal places, e.g. 9.472 → "9.47". The per-arrow average convention
 * (group size uses one decimal, arrow averages two — a real distinction, not
 * drift: an arrow average of 9.47 vs 9.48 is a meaningful difference over a
 * season, a group size of 2.3" vs 2.4" is not).
 */
export function twoDecimals(n, fallback = NO_VALUE) {
  return Number.isFinite(n) ? n.toFixed(2) : fallback;
}

/**
 * One decimal place with a trailing ".0" trimmed, e.g. 9.47 → "9.5" but
 * 10.0 → "10". For dense chart labels and tooltips where the extra glyph
 * costs more than the alignment gains.
 */
export function trimDecimal(n, fallback = NO_VALUE) {
  if (!Number.isFinite(n)) return fallback;
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/**
 * Thousands-separated integer, e.g. 12345 → "12,345". Locale-pinned to en-US
 * so the separator does not shift with the browser locale — the rest of the
 * app's number and date formatting is en-US too.
 */
export function formatCount(n, fallback = NO_VALUE) {
  return Number.isFinite(n) ? n.toLocaleString("en-US") : fallback;
}
